import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { formatChartTitle, formatTrendPercent } from "../../utils";

interface TextStatProps {
  title: string;
  value: string;
  change?: string;
  unit?: string;
}

export const TextStat: React.FC<TextStatProps> = ({
  title,
  value,
  change,
  unit,
}) => {
  const theme = useTheme();
  const formattedTitle = formatChartTitle(title);
  const normalizedChange = formatTrendPercent(change) ?? "";
  const isNegativeChange = normalizedChange.trim().startsWith("-");
  const isPositiveChange = normalizedChange.trim().startsWith("+");
  const trendColor = isNegativeChange
    ? theme.palette.error.main
    : isPositiveChange
      ? theme.palette.success.main
      : theme.palette.text.secondary;
  const TrendIcon = isNegativeChange
    ? TrendingDownIcon
    : isPositiveChange
      ? TrendingUpIcon
      : TrendingFlatIcon;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        {formattedTitle}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 0.75,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <Typography
            variant="subtitle1"
            component="span"
            sx={{
              fontWeight: 700,
            }}
          >
            {value}
          </Typography>
          {unit && (
            <Typography variant="caption" color="text.secondary">
              {unit}
            </Typography>
          )}
        </Box>
        {change && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              color: trendColor,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              {normalizedChange}
            </Typography>
            <TrendIcon
              sx={{
                fontSize: "0.875rem",
                color: trendColor,
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
